/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from 'react'
import {GroupSubNameItem} from 'utils/upsellsUtils'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {getSelectedUpsell} from 'utils/utility'
import {useTypedSelector} from 'helpers/reducers'
import UpsellPriceListSimpleRow from 'pages/Front/Booking/components/PackageUpsell/UpsellPriceListSimpleRow'
import UpsellPriceListCombinationRow from './UpsellPriceListCombinationRow'

interface UpsellPriceListSectionProps {
    groupName: string
    subGroups: GroupSubNameItem[]
    isCompact?: boolean
}

export const UpsellPriceListSection = (props: UpsellPriceListSectionProps) => {
    const { t } = useTranslation()
    const [isCollapsed, setIsCollapsed] = useState(false)
    const bookingProcess = useTypedSelector((state) => state.bookingState.bookingProcess)

    const subGroups = props.subGroups || []

    const hasAnySelected = subGroups.some((subGroup) =>
        subGroup.items?.some((groupedItem) =>
            groupedItem.items?.some(
                (packageItem) => getSelectedUpsell(bookingProcess.upsell, packageItem) !== undefined
            )
        )
    )

    const sectionLabel = props.groupName === 'favorites' ? t('Favorites') : props.groupName
    const totalItemsCount = subGroups.reduce((acc, subGroup) => acc + (subGroup.items?.length || 0), 0)

    return (
        <section
            className={clsx(
                'UpsellPriceListSection overflow-hidden rounded-3xl bg-white dark:bg-gray-950 shadow-sm ring-1',
                hasAnySelected
                    ? 'ring-blue-300 dark:ring-blue-700'
                    : 'ring-slate-200 dark:ring-slate-700'
            )}
        >
            {/* Sticky header */}
            <button
                className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-gray-950/95 px-5 py-4 text-left backdrop-blur"
                type="button"
                onClick={() => setIsCollapsed((prev) => !prev)}
            >
                <div>
                    <h2 className="text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-slate-100">
                        {sectionLabel}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {totalItemsCount === 1
                            ? t('1 item available')
                            : t('{{count}} items available', { count: totalItemsCount })}
                    </p>
                </div>
                <span
                    className={clsx(
                        'flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 transition-transform duration-200',
                        isCollapsed ? '' : 'rotate-180'
                    )}
                >
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            clipRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            fillRule="evenodd"
                        />
                    </svg>
                </span>
            </button>

            {/* Items */}
            {!isCollapsed && (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {subGroups.map((subGroup) => (
                        <div key={subGroup.subGroupName || 'default'}>
                            {subGroup.subGroupName && (
                                <div className="bg-slate-200 dark:bg-slate-900/50 px-5 py-2 border-b border-slate-100 dark:border-slate-800">
                                    <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-900 dark:text-slate-400">
                                        {subGroup.subGroupName}
                                    </h3>
                                </div>
                            )}
                            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                                {subGroup.items?.map((groupedItem, index) => {
                                    const isCombination =
                                        groupedItem.variant_id !== null && (groupedItem.items?.length || 0) > 1
                                    const itemKey =
                                        groupedItem.variant_id !== null
                                            ? `combo-variant-${groupedItem.variant_id}`
                                            : `simple-${groupedItem.items?.map((i) => i.id).join('-') || ''}-${index}`

                                    if (isCombination) {
                                        return (
                                            <li className={"UpsellPriceListCombinationRowItem"} key={itemKey}>
                                                <UpsellPriceListCombinationRow
                                                    isCompact={props.isCompact}
                                                    item={groupedItem}
                                                />
                                            </li>
                                        )
                                    }

                                    return (
                                        <li className={"UpsellPriceListSimpleRow"} key={itemKey}>
                                            <UpsellPriceListSimpleRow
                                                isCompact={props.isCompact}
                                                item={groupedItem}
                                            />
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </section>
    )
}

export default UpsellPriceListSection
